/**
 * Derives deduped, sorted role IDs from base and add-on roles.
 *
 * @param baseRoles - the user's base roles
 * @param addOnRoles - the user's add-on roles
 *
 * @returns comma-separated, alphabetically sorted, deduped role IDs
 */
export function buildUserRoles(baseRoles: { roleId: string }[], addOnRoles: { roleId: string }[]): string {
    const baseRoleIds = baseRoles.map((r) => r.roleId);
    const addOnRoleIds = addOnRoles.map((r) => r.roleId);
    return Array.from(new Set([...baseRoleIds, ...addOnRoleIds]))
        .sort()
        .join(',');
}

/**
 * Maps a debug environment name to one of the three Unleash environment values.
 *
 * @param debugEnvName - the debug environment name from ConfigService
 *
 * @returns one of 'development', 'stage', 'production'
 */
export function resolveEnvironment(debugEnvName: string): string {
    if (debugEnvName === 'stage') {
        return 'stage';
    }

    if (debugEnvName === 'production') {
        return 'production';
    }

    return 'development';
}

/**
 * Determines the deployment target based on the environment and the browser hostname.
 *
 * @param environment - the resolved environment value
 * @param hostname - the current browser hostname
 *
 * @returns one of 'local', 'preview', 'development', 'stage', 'production'
 */
export function resolveHost(environment: string, hostname: string): string {
    if (environment !== 'development') {
        return environment;
    }

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'local';
    }

    if (hostname.endsWith('.amplifyapp.com')) {
        return 'preview';
    }

    return 'development';
}

/**
 * Determines which backend environment the app is connected to.
 *
 * @param host - the resolved host value
 * @param apiBaseUrl - the API base URL from ConfigService
 *
 * @returns one of 'development', 'stage', 'production'
 */
export function resolveBackend(host: string, apiBaseUrl: string): string {
    if (host === 'stage') {
        return 'stage';
    }

    if (host === 'production') {
        return 'production';
    }

    if (host === 'preview' || host === 'development') {
        return 'development';
    }

    if (apiBaseUrl.includes('.stage.')) {
        return 'stage';
    }

    return 'development';
}
